// src/services/api.js — single axios instance shared across the whole app.
// Automatically attaches the JWT (from localStorage) to every request,
// and automatically logs the user out on a 401 (expired/invalid token).
import axios from "axios";

const configuredApiUrl = process.env.REACT_APP_API_URL?.trim();
const isProduction = process.env.NODE_ENV === "production";
const isLoopbackApiUrl = (url) => /^https?:\/\/(?:localhost|127(?:\.\d{1,3}){3}|\[?::1\]?)(?::\d+)?(?:\/|$)/i.test(url);
const defaultApiUrl = isProduction
  ? "https://itontine-backend.onrender.com/api"
  : "http://localhost:5000/api";
const apiBaseUrl = ((configuredApiUrl && !(isProduction && isLoopbackApiUrl(configuredApiUrl)))
  ? configuredApiUrl
  : defaultApiUrl)
  .replace(/\s*Environment\s*:\s*Production\s*$/i, "")
  .replace(/\/+$/, "");

if (!/^https?:\/\/[^/\s]+(?:\/[^/\s]*)*\/api$/i.test(apiBaseUrl)) {
  throw new Error(
    "Invalid REACT_APP_API_URL. Set it to the backend URL, for example https://your-service.onrender.com/api."
  );
}

const api = axios.create({
  baseURL: apiBaseUrl,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("itontine_token") || sessionStorage.getItem("itontine_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || "";
    const isAuthenticationRequest = /\/auth\/(?:login|register|forgot-password|reset-password)(?:\?|$)/.test(requestUrl);
    const hadSessionToken = Boolean(error.config?.headers?.Authorization);
    if (error.response?.status === 401 && hadSessionToken && !isAuthenticationRequest) {
      localStorage.removeItem("itontine_token");
      localStorage.removeItem("itontine_user");
      sessionStorage.removeItem("itontine_token");
      sessionStorage.removeItem("itontine_user");
      if (window.location.pathname !== "/login" || !window.location.search.includes("reason=session-expired")) {
        window.location.assign("/login?reason=session-expired");
      }
    }
    return Promise.reject(error);
  }
);

export default api;
